import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthGate } from "@/components/auth/AuthGate";
import { ComplexIngestionUploadPage } from "@/pages/upload/ComplexIngestionUploadPage";
import { HomePage } from "@/pages/HomePage";
import { KnownMappingsPage } from "@/pages/KnownMappingsPage";
import { HistoryPage } from "@/pages/HistoryPage";
import { SchemaBuilderPage } from "@/pages/SchemaBuilder/SchemaBuilderPage";
import { SchemaCreatePage } from "@/pages/SchemaBuilder/SchemaCreatePage";
import { SchemaCreateFromFilePage } from "@/pages/SchemaBuilder/SchemaCreateFromFilePage";
import { SchemaTemplateSetupPage } from "@/pages/SchemaBuilder/SchemaTemplateSetupPage";
import { SchemaScratchSetupPage } from "@/pages/SchemaBuilder/SchemaScratchSetupPage";
import { SchemaBuilderEditPage } from "@/pages/SchemaBuilder/SchemaBuilderEditPage";
import { ProvidersListPage } from "@/pages/providers/ProvidersListPage";
import { AddProviderPage } from "@/pages/providers/AddProviderPage";
import { ProviderProfilePage } from "@/pages/providers/ProviderProfilePage";
import { useAuthToken } from "@/hooks/useAuthToken";
import { CurrentUserProvider } from "@/hooks/CurrentUserProvider";
import { isMsalActive } from "@/config/auth";
import { UploadProvider } from "@/hooks/useUpload";
import { trackPageView } from "@/services/telemetry";
import { useProviderPermissions } from "@/hooks/useProviderPermissions";

/** Human-readable names for each route -- used in AppInsights page view telemetry. */
const PAGE_NAMES: Record<string, string> = {
  "/": "Home",
  "/pipeline": "Workspace",
  "/known-mappings": "Known Mappings",
  "/history": "Processing History",
  "/schema-builder": "Schema Builder",
  "/provider-management/providers": "Provider Management - Providers",
  "/provider-management/providers/new": "Provider Management - Add Provider",
  "/provider-management/ingestion-history": "Provider Management - Ingestion History",
  "/provider-management/known-mappings": "Provider Management - Known Mappings",
};

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    const name = location.pathname.startsWith("/schema-builder/create/file")
      ? "Schema Builder Create From File"
      : location.pathname.startsWith("/schema-builder/create")
        ? "Schema Builder Create"
        : location.pathname === "/schema-builder"
          ? "Schema Builder"
          : location.pathname.startsWith("/schema-builder")
            ? "Schema Builder Edit"
            : location.pathname.match(/^\/provider-management\/providers\/[^/]+$/)
              ? "Provider Management - Provider Profile"
              : (PAGE_NAMES[location.pathname] ?? location.pathname);
    trackPageView(name, location.pathname, {
      search: location.search || undefined,
    });
  }, [location.pathname, location.search]);

  return null;
}

// Registers the MSAL token provider. Rendered only when MSAL is active, so it is
// never mounted (and useMsal never runs) under the Databricks Apps deploy target.
function MsalAuthBootstrap() {
  useAuthToken();
  return null;
}

/**
 * Global Known Mappings (cross-provider) — App.ADMIN / App.PROVIDER_VIEWER only.
 * Per-provider embeds still use KnownMappingsPage directly without this gate.
 */
function GlobalKnownMappingsRoute() {
  const { canViewGlobalKnownMappings } = useProviderPermissions();
  if (!canViewGlobalKnownMappings) {
    return <Navigate to="/" replace />;
  }
  return <KnownMappingsPage />;
}

/**
 * Global Ingestion History (cross-provider) — App.ADMIN only.
 * Per-provider embeds still use HistoryPage with a providerId prop without this gate.
 */
function GlobalIngestionHistoryRoute() {
  const { canViewGlobalIngestionHistory } = useProviderPermissions();
  if (!canViewGlobalIngestionHistory) {
    return <Navigate to="/" replace />;
  }
  return <HistoryPage />;
}

export function App() {
  return (
    <CurrentUserProvider>
      {isMsalActive && <MsalAuthBootstrap />}
      <AuthGate>
        <UploadProvider>
          <PageViewTracker />
          <Routes>
            <Route element={<AppLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/pipeline" element={<ComplexIngestionUploadPage />} />
              <Route path="/mapping" element={<Navigate to="/pipeline" replace />} />
              <Route path="/data-quality" element={<Navigate to="/pipeline" replace />} />
              <Route path="/known-mappings" element={<GlobalKnownMappingsRoute />} />
              <Route path="/schema-builder" element={<SchemaBuilderPage />} />
              <Route path="/schema-builder/create" element={<SchemaCreatePage />} />
              <Route path="/schema-builder/create/file" element={<SchemaCreateFromFilePage />} />
              <Route path="/schema-builder/create/template" element={<SchemaTemplateSetupPage />} />
              <Route path="/schema-builder/create/scratch" element={<SchemaScratchSetupPage />} />
              <Route path="/schema-builder/new" element={<SchemaBuilderEditPage />} />
              <Route path="/schema-builder/:schemaId" element={<SchemaBuilderEditPage />} />
              <Route path="/provider-management/providers" element={<ProvidersListPage />} />
              <Route path="/provider-management/providers/new" element={<AddProviderPage />} />
              <Route
                path="/provider-management/providers/:providerId"
                element={<ProviderProfilePage />}
              />
              <Route
                path="/provider-management/ingestion-history"
                element={<GlobalIngestionHistoryRoute />}
              />
              <Route
                path="/provider-management/known-mappings"
                element={<GlobalKnownMappingsRoute />}
              />
            </Route>
          </Routes>
        </UploadProvider>
      </AuthGate>
    </CurrentUserProvider>
  );
}
